<template>
  <NuxtLayout name="pdp" :page-name="store.product?.ProductName">
    <section
      class="w-full mt-20 lg:grid lg:grid-cols-2 lg:gap-24 flex flex-col gap-24"
    >
      <!-- Vänster kolumn -->
      <div class="flex flex-col gap-24">
        <div class="order-2 lg:order-1">
          <PdpImages />
        </div>

        <div class="flex flex-col gap-8 md:gap-16 order-1 lg:order-2">
          <PdpProductTitleSection />
          <PdpLabelList />
        </div>

        <PdpUspList class="order-3 lg:order-3" />
      </div>

      <!-- Höger kolumn -->
      <div class="flex flex-col gap-24">
        <PdpColorSelection class="order-4 lg:order-1" />
        <div class="order-6 lg:order-2">
          <PdpMainInfo />
        </div>
        <PdpChargingInfo class="order-5 lg:order-3" />
      </div>

      <div
        v-if="
          store.currentBundlePrice?.Discounts?.find(
            (discount: any) => discount.Description.length > 0,
          ) !== undefined
        "
        class="order-7 lg:col-start-1 lg:row-start-auto"
      >
        <PdpDiscounts />
      </div>

      <PdpInfoAccordions class="order-8 lg:col-start-1 lg:row-start-auto" />
    </section>

    <PdpPriceFooterAccessory />
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { usePdpSEOMetaOgData } from '~/composables/pdp/seoInput';
import { usePdpListenToViewItem } from '~/composables/tracking/usePdpListenToViewItem';
import { general_setPageTheme } from '~/utils/general/pageThemeClassFromMarkup';

const store = useMyProductPageStore();
const route = useRoute();
const notifications = useNotificationStore();
const { $api } = useNuxtApp();
notifications.reset();

definePageMeta({
  layout: false,
});

usePdpListenToViewItem({
  listenToColor: true,
  listenToDownpayTime: false,
  listenToPlanId: false,
  listenToStorage: false,
});

const { data, error } = await useAsyncData(
  route.fullPath,
  async () => {
    const productData = await $api.accessoryPage.getAccessoryByProductLinkUrl(
      route.path,
    );
    return productData;
  },
  {
    transform: cache_addFetchedAtTransform,
    getCachedData: cache_getCachedDataFromServer,
  },
);

if (error.value) {
  if (error.value.statusCode === 404) {
    const pathSegments = route.path.split('/');
    const path = pathSegments.slice(0, -1).join('/');
    await navigateTo(path, {
      redirectCode: 302,
      external: true,
    });
  }
  notifications.setStandardErrorNotification();
}

if (data && data.value) {
  store.populatePACState(data.value, route.query);
}

usePdpSEOMetaOgData({ productCategory: 'accessory' });

if (store.product?.ShowOnHomePage === false) {
  useHead({
    meta: [{ name: 'robots', content: 'noindex, nofollow' }],
  });
}

general_setPageTheme(data.value?.Description);

const currentProductVariantListenable = computed(() => store.currentVariant);
watch(
  currentProductVariantListenable,
  (variant) => {
    if (variant?.DisableBuyButton === true) {
      notifications.setNewNotification({
        type: 'error',
        title: 'Slutsåld',
        msg: 'Den här produktvarianten är antingen slutsåld eller otillgänglig just nu. Testa övriga kombinationer!',
        isCloseable: true,
        link: {
          url: 'https://www.halebop.se/support',
          text: 'Kontakta supporten',
        },
      });
    } else {
      if (notifications.title === 'Slutsåld') {
        notifications.reset();
      }
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped></style>
